/**
 * School Advanced-Lists Catalog API Contract
 *
 * GET /v1/schools/:school_public_id/lists
 * Returns the catalog of Blackbaud advanced lists synced for the school.
 * Used as the left-pane data source in the Lists Explorer (U7).
 */

import { z } from 'zod'

/**
 * Backend-DERIVED catalog health for one mirrored Blackbaud list. Never stored
 * (KTD6) — a list that reappears is `live` again on the next read with no
 * manual step, so nothing may cache this.
 *
 *   - `live`    — the catalog still returns it, or it has not missed enough
 *                 corroborated-complete fetches to say otherwise.
 *   - `missing` — repeated COMPLETE fetches have not returned it AND its last
 *                 sighting is older than the time floor.
 *
 * Deliberately NOT called "deleted". Blackbaud answers a request for a deleted
 * list, an unshared list and a permission-lost list identically (`200
 * {count:0}`), so the only truthful claim is about what we observed.
 *
 * Precedent for a backend-derived health enum: `ListMembershipDisplayStateSchema`.
 */
export const ListCatalogStatusSchema = z.enum(['live', 'missing'])

export type ListCatalogStatus = z.infer<typeof ListCatalogStatusSchema>

export const AdvancedListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  description: z.string(),
  category: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * Derived at read time. The catalog NEVER excludes a `missing` row (KTD3) —
   * it annotates, and the client renders it disabled with a reason.
   */
  catalogStatus: ListCatalogStatusSchema,
  /**
   * ISO timestamp of the last time this list actually appeared in a fetched
   * Blackbaud catalog, or **null when it has never once been observed**.
   *
   * NULL is not "unknown, assume today" and it is not the row's creation date:
   * the backend derivation anchors its time floor on `createdAt` when there is
   * no sighting, but that anchor is deliberately NOT surfaced here. Printing a
   * mirror-row creation date as "last seen" asserts an observation that never
   * happened. A `missing` list with a null here needs different copy ("Never
   * confirmed in Blackbaud") from one with a date ("Not seen in Blackbaud
   * since 12 Jul").
   */
  lastSeenInCatalogAt: z.string().nullable()
})

export type AdvancedListItem = z.infer<typeof AdvancedListItemSchema>

export const AdvancedListsResponseSchema = z.object({
  data: z.array(AdvancedListItemSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    message: z.string().optional()
  }),
  message: z.string()
})

export type AdvancedListsResponse = z.infer<typeof AdvancedListsResponseSchema>
